import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import {titleize} from "../../../utils/strings";
import CountrySelect from "../../shared/CountrySelect";
import StateSelect from '../../shared/StateSelect';
import { CustomerType } from '../../../propTypes';
import {getCountries, getStates, findCountry, getStateDisplayName, getZipCodeDisplayName} from '../../../utils/i18n';

import { ensureKeyed } from 'utils';

export default class PaymentDetailsInput extends React.Component {
  static propTypes = {
    onChange: PropTypes.func.isRequired, // Callback when this input is updated.  Should pass data and `valid` flag.
    customer: CustomerType.isRequired,
    includeEmail: PropTypes.bool,
    disabled: PropTypes.bool
  };

  static defaultProps = {
    includeEmail: false,
    disabled: false
  };

  constructor() {
    super();

    this.state = {
      ...this.state,
      valid: false,
      name: undefined,
      address_line1: undefined,
      address_city: undefined,
      address_state: undefined,
      address_zip: undefined,
      address_country: getCountries().find(c => c.stripeValue).stripeValue // Gets the first country configured for this customer.
    };
  }

  componentDidMount() {
    this.checkValidity();
  }

  componentWillUnmount() {
    this.props.onChange({});
  }

  handleChange(fieldName) {
    return (event) => this.updateField(fieldName, event.target.value);
  }

  updateField(name, value) {
    this.setState({
      [name]: value
    }, () => {
      this.checkValidity();
    })
  }

  hasStates() {
    const {address_country} = this.state;
    const country = findCountry({stripeValue: address_country});

    return !!country && getStates(country).some(x=>x);
  }

  hasZipCode() {
    const {address_country} = this.state;
    const country = findCountry({stripeValue: address_country});

    return !!country && !!country.zipCodeDisplayName;
  }

  checkValidity() {
    const { customer, includeEmail, onChange } = this.props;
    const {
      name,
      email,
      address_line1,
      address_city,
      address_country,
      address_state,
      address_zip
    } = this.state;

    const hasName = !!name;
    const hasEmail = !includeEmail || !!email;

    const hasAddress = !customer.enable_credit_card_address_verification
      || (
        !!address_line1
        && !!address_city
        && !!address_country
        && (!this.hasStates() || !!address_state)
        && (!this.hasZipCode() || !!address_zip)
      );

    const valid = hasName && hasEmail && hasAddress;

    this.setState(
      { valid },
      () => onChange(this.state)
    );
  }

  fieldFor(name, options={}) {
    const {displayName, ...opts} = options;

    return <label className={`payment-details-field payment-details-${name}`}>
      <span className={'payment-input-label'}>
        {displayName || titleize(name)}:
      </span>
      <input className={'payment-input-field'}
             type={opts.type || 'text'}
             name={name}
             defaultValue={this.state[name]}
             onChange={this.handleChange(name)}
             {...opts}
      />
    </label>;
  }

  selectFor(name, options={}) {
    const {ControlClass = Select, displayName, ...opts} = options;

    return <label className={`payment-details-field payment-details-${name}`}>
      <span className={'payment-input-label'}>
        {displayName || titleize(name)}:
      </span>
      <ControlClass className={'payment-input-field'}
                    onChange={({value}) => this.updateField(name, value)}
                    value={this.state[name]}
                    clearable={false}
                    {...opts}
      />
    </label>;
  }

  render() {
    const { customer, includeEmail, disabled } = this.props;
    const {address_country} = this.state;
    const country = findCountry({stripeValue: address_country});

    return <React.Fragment>
      {this.fieldFor('name', {
        displayName: 'Name',
        placeholder: 'Full Name of Account Holder',
        disabled
      })}

      {includeEmail && this.fieldFor('email', {
        displayName: 'Email Address',
        placeholder: 'Email Address of Account Holder',
        disabled
      })}

      {customer.enable_credit_card_address_verification &&
        [
          this.fieldFor('address_line1', {
            displayName: 'Address',
            disabled
          }),
          this.fieldFor('address_city', {
            displayName: 'City',
            disabled
          }),
          this.selectFor('address_country', {
            ControlClass: CountrySelect,
            filter: (c) => c.stripeValue,
            getOptionValue: (c) => c.stripeValue,
            displayName: 'Country',
            disabled
          }),
          this.hasStates() && this.selectFor('address_state', {
            ControlClass: StateSelect,
            country: country,
            displayName: getStateDisplayName(country),
            placeholder: `Select a ${getStateDisplayName(country)}...`,
            disabled
          }),
          this.hasZipCode() && this.fieldFor('address_zip', {
            displayName: getZipCodeDisplayName(country),
            disabled
          }),
        ].filter(x=>x).map(ensureKeyed())
      }
    </React.Fragment>;
  }
}
